import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import get from 'lodash.get';
import { Paginator, Grid } from '@kis-technologies/kis-ui';
import EmailTicketImage from 'assets/EmailTicket.png';
import TextTicketImage from 'assets/TextTicket.png';
import PrintTicketImage from 'assets/PrintTicket.png';
import { Footer } from 'components/Footer';
import { CardContainer } from 'components/CardContainer';
import { TileLayout } from 'components/TileLayout';
import { ArrowPagination } from 'components/Pagination';
import { selectors as configSelectors, Configuration, Messages, TicketDeliverySettings } from 'services/configuration';
import { selectors as ticketingSelectors, DeliveryMethod } from 'services/ticketing';
import { useStyles } from '../styled';
import { ComponentProps } from '../types';

export const DeliverySelection = (props: ComponentProps) => {
  const { onNext, onPrev } = props;
  const { t, i18n } = useTranslation();

  const config = useSelector(configSelectors.selectConfiguration) as Configuration;
  const ticketingPath = useSelector(ticketingSelectors.selectTicketingConfigPath);

  const touchForward = get(config, 'configuration.globalSettings.touchForward.ticketDelivery.enabled');
  const ticketDeliverySettings: TicketDeliverySettings = get(config, `${ticketingPath}.ticketDeliverySettings`, {});
  const tileOrder: string[] = get(ticketDeliverySettings, 'deliverySelection.tileOrder', []);
  const messages: Messages = get(ticketDeliverySettings, 'deliverySelection.messages', {});
  const headerText: string = get(messages, `header.${i18n.language}`, t('delivery.selectYourTicketDelivery'));
  const backButtonText: string = get(messages, `buttons.${i18n.language}[0]`, t('commonText.back'));
  const nextButtonText: string = get(messages, `buttons.${i18n.language}[1]`, t('commonText.continue'));
  const emailDelivery: TicketDeliverySettings['email'] = get(ticketDeliverySettings, 'email', {});
  const emailDeliveryEnabled: boolean = get(emailDelivery, 'enabled', false);
  const emailTileText: string = get(emailDelivery, `tileText.${i18n.language}`, t('delivery.emailTickets'));
  const textDelivery: TicketDeliverySettings['textMessage'] = get(ticketDeliverySettings, 'textMessage', {});
  const textDeliveryEnabled: boolean = get(textDelivery, 'enabled', false);
  const textTileText: string = get(textDelivery, `tileText.${i18n.language}`, t('delivery.textTickets'));
  const printDelivery: TicketDeliverySettings['print'] = get(ticketDeliverySettings, 'print', {});
  const printDeliveryEnabled: boolean = get(printDelivery, 'enabled', false);
  const printTileText: string = get(printDelivery, `tileText.${i18n.language}`, t('delivery.printTickets'));

  const classes = useStyles({})();

  const [selectedDelivery, setSelectedDelivery] = useState<DeliveryMethod | null>(null);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [enabledDeliveryMethods, setEnabledDeliveryMethods] = React.useState<DeliveryMethod[]>([]);

  useEffect(() => {
    const methods: DeliveryMethod[] = [];
    if (emailDeliveryEnabled) methods.push('email');
    if (textDeliveryEnabled) methods.push('textMessage');
    if (printDeliveryEnabled) methods.push('print');

    sortDeliveryMethods(methods);
    setEnabledDeliveryMethods(methods);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailDeliveryEnabled, textDeliveryEnabled, printDeliveryEnabled]);

  useEffect(() => {
    if (selectedDelivery && touchForward) {
      onNextPageClick();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDelivery]);

  /**
   * Sorts the delivery methods based on the tileOrder configuration.
   */
  const sortDeliveryMethods = (methods: DeliveryMethod[]) => {
    if (!tileOrder.length) return;

    methods.sort((a, b) => {
      const aIndex = tileOrder.indexOf(a);
      const bIndex = tileOrder.indexOf(b);

      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;

      return aIndex - bIndex;
    });
  };

  const onNextPageClick = () => {
    onNext(selectedDelivery as DeliveryMethod, '');
  };

  return (
    <>
      <TileLayout headerText={headerText}>
        <Paginator
          paginationComponent={ArrowPagination}
          items={enabledDeliveryMethods}
          itemsPerPage={3}
          page={currentPage}
          onPageChange={(newPage) => setCurrentPage(newPage)}
        >
          {({ items }) => (
            <Grid container className={classes.selectionCardContainer}>
              {items.map((item) => {
                switch (item) {
                  case 'email':
                    /* Card for email delivery */
                    return (
                      <CardContainer
                        key={'email'}
                        imgSrc={EmailTicketImage}
                        cardAction={() => setSelectedDelivery('email')}
                        descText={emailTileText}
                        altText={'email'}
                        isSelected={selectedDelivery === 'email'}
                      />
                    );
                  case 'textMessage':
                    /* Card for text message delivery */
                    return (
                      <CardContainer
                        key={'textMessage'}
                        imgSrc={TextTicketImage}
                        cardAction={() => setSelectedDelivery('textMessage')}
                        descText={textTileText}
                        altText={'textMessage'}
                        isSelected={selectedDelivery === 'textMessage'}
                      />
                    );
                  case 'print':
                  default:
                    /* Card for print delivery */
                    return (
                      <CardContainer
                        key={'print'}
                        imgSrc={PrintTicketImage}
                        cardAction={() => setSelectedDelivery('print')}
                        descText={printTileText}
                        altText={'print'}
                        isSelected={selectedDelivery === 'print'}
                      />
                    );
                }
              })}
            </Grid>
          )}
        </Paginator>
      </TileLayout>
      {/* Footer */}
      <Footer
        onPrevClick={onPrev}
        prevText={backButtonText}
        onNextClick={() => onNextPageClick()}
        nextText={touchForward ? undefined : nextButtonText}
        isNextDisabled={!selectedDelivery}
      />
    </>
  );
};
